<script lang="ts" setup>
import type { ClassNameValue } from 'tailwind-merge'
import { computed, useAttrs } from 'vue'
import { tv } from 'tailwind-variants'
import { getBaseColorName } from '~/utils/colors'
import { buttonTheme } from '~/components/ui/variants/buttonTheme'

export type ButtonVariants = 'solid' | 'outline' | 'ghost'
export type ButtonShades = 'dark' | 'light'
export type ButtonColors
  = 'brand'
    | 'gray'
    | 'orange'
    | 'coral'
    | 'violet'
    | 'purple'
    | 'blue'
    | 'seagreen'
    | 'green'
    | 'white'
    | 'yellow'
    | 'teacher'
export type ButtonSizes = 'sm' | 'md' | 'lg' | 'xl'
export type ButtonShapes = 'square'

const {
  variant,
  shade,
  color,
  size,
  shape,
  href,
  fullWidth,
  center,
} = defineProps<Props>()

const attrs = useAttrs()

const tag = computed(() => {
  if (href)
    return 'a'
  if ('to' in attrs)
    return 'router-link'
  if (attrs.type === 'label')
    return 'label'
  return 'button'
})
const externalClass = computed(() => attrs.class as ClassNameValue)
const componentAttrs = computed(() => ({
  type: 'button',
  ...(href && { href }),
}))
const normalizedColor = computed(() => {
  const base = color && getBaseColorName(color as string)
  return (base || undefined) as ButtonColors | undefined
})

const button = tv({
  extend: buttonTheme,
})

interface Props {
  variant?: ButtonVariants
  shade?: ButtonShades
  color?: ButtonColors | string
  size?: ButtonSizes
  shape?: ButtonShapes
  href?: string
  fullWidth?: boolean
  center?: boolean
}
</script>

<template>
  <component
    :is="tag"
    :class="button({ variant, shade, color: normalizedColor, size, shape, fullWidth, center, class: externalClass })"
    data-ignore-tts
    v-bind="componentAttrs"
  >
    <slot />
  </component>
</template>
